/**
 * Pure "what's still missing" checks for the TikTok workspace. They drive the
 * rail's red step icon, the ad group "incomplete" pill and the ad "N missing"
 * pill. The save helpers re-run the same rules (plus live checks such as
 * video transcoding) before anything hits TikTok.
 */
import type { WorkspaceAd, WorkspaceAdGroup, WorkspaceCampaign } from '@/contexts/TiktokWorkspaceContext';
import {
  adDestinationFor, defaultGoalFor, goalSpec, objectiveSpec,
} from '@/lib/tiktok-matrix';
import { AD_TEXT_MAX, adForm, groupForm, groupPlacementsOf, identityFitsPlacement, type Identity } from './workspaceShared';

export function campaignIssues(campaign: WorkspaceCampaign): string[] {
  if (campaign.isLocked) return [];
  const fd = campaign.formData as Record<string, string>;
  const issues: string[] = [];
  const name = (fd.campaignName ?? '').trim();
  const objective = fd.objectiveType ?? '';
  const budgetMode = fd.budgetMode ?? 'BUDGET_MODE_DAY';
  if (!name) issues.push('Campaign name is required.');
  if (!objective || !objectiveSpec(objective)) issues.push('Pick a valid objective.');
  const spec = objectiveSpec(objective);
  if (spec?.unavailableReason) issues.push(`${spec.label}: ${spec.unavailableReason}`);
  if (budgetMode !== 'BUDGET_MODE_INFINITE') {
    const n = Number(fd.budget ?? '');
    if (!fd.budget?.trim() || !Number.isFinite(n) || n <= 0) issues.push('Enter the campaign budget.');
  }
  return issues;
}

/** Short "needs …" fragments for one ad group. Empty when it can be created. */
export function adGroupIssues(group: WorkspaceAdGroup, objective: string | undefined): string[] {
  if (group.isLocked) return [];
  const f = groupForm(group);
  const issues: string[] = [];
  const name = ((f.adGroupName as string) ?? '').trim();
  const placements = groupPlacementsOf(group);
  const locationIds = Array.isArray(f.locationIds) ? (f.locationIds as string[]) : [];
  const goalCode = (f.optimizationGoal as string) || defaultGoalFor(objective);
  const goal = goalSpec(objective, goalCode);
  const bidType = f.bidType === 'BID_TYPE_CUSTOM' ? 'BID_TYPE_CUSTOM' : 'BID_TYPE_NO_BID';

  if (!name) issues.push('a name');
  if (placements.length === 0) issues.push('a placement');
  if (locationIds.length === 0) issues.push('a location');
  if (!objective) issues.push('a campaign objective');
  else if (!goal) issues.push('a valid optimisation goal');
  const budget = Number(f.budget ?? '');
  if (!Number.isFinite(budget) || budget <= 0) issues.push('a budget');
  if (bidType === 'BID_TYPE_CUSTOM') {
    const bid = Number(f.bidPrice ?? '');
    if (!Number.isFinite(bid) || bid <= 0) issues.push('a bid cap');
  }
  if (goal?.requires.includes('PIXEL_EVENT')) {
    if (!f.pixelId) issues.push('a pixel');
    if (!f.optimizationEvent) issues.push('a pixel event');
  }
  if (goal?.requires.includes('APP') && !f.appId) issues.push('an app');
  if (goal?.requires.includes('FREQUENCY_CAP')) {
    const fr = Number(f.frequency ?? '');
    const fs = Number(f.frequencySchedule ?? '');
    if (!Number.isFinite(fr) || fr < 1 || !Number.isFinite(fs) || fs < 1) issues.push('a frequency cap');
  }
  if (!f.scheduleStartTime || !Number.isFinite(new Date(f.scheduleStartTime as string).getTime())) issues.push('a valid start time');
  if (f.scheduleType === 'SCHEDULE_START_END') {
    if (!f.scheduleEndTime || !Number.isFinite(new Date(f.scheduleEndTime as string).getTime())) issues.push('a valid end time');
    else if (new Date(f.scheduleEndTime as string) <= new Date(f.scheduleStartTime as string)) issues.push('an end time after the start');
  }
  return issues;
}

/** Short "needs …" fragments for one ad. Empty when it can be created. */
export function adIssues(
  ad: WorkspaceAd,
  group: WorkspaceAdGroup,
  objective: string | undefined,
  identities?: Identity[],
): string[] {
  if (ad.isLocked) return [];
  const f = adForm(ad);
  const g = groupForm(group);
  const issues: string[] = [];
  const tiktokPlacement = groupPlacementsOf(group).includes('PLACEMENT_TIKTOK');
  const adFormat = (f.adFormat as string) ?? 'SINGLE_VIDEO';
  const groupGoal = (g.optimizationGoal as string) || defaultGoalFor(objective);
  const destination = adDestinationFor(objective, groupGoal);

  if (!((f.adName as string) ?? '').trim()) issues.push('a name');
  if (!((f.adText as string) ?? '').trim()) issues.push('ad text');
  if (((f.adText as string) ?? '').length > AD_TEXT_MAX) issues.push(`ad text of ${AD_TEXT_MAX} characters or less`);

  const identityId = (f.identityId as string) ?? '';
  if (!identityId) issues.push('a TikTok identity');
  else if (identities) {
    const type = identities.find((i) => i.identityId === identityId)?.identityType;
    if (!type) issues.push('an available TikTok identity');
    if (type && !identityFitsPlacement(type, tiktokPlacement)) issues.push('an identity allowed on this placement');
  }

  if (destination === 'required' && !validLandingUrl(f.landingPageUrl)) issues.push('a valid website URL (https://…)');

  if (destination === 'form' && !f.pageId) issues.push('an Instant Form');

  if (adFormat === 'SINGLE_VIDEO') {
    if (!f.resolvedVideoId) issues.push('the video');
    else {
      const gate = f.videoGate as { state?: string; reason?: string } | undefined;
      if (gate?.state === 'rejected') issues.push('a video that meets TikTok\'s specs');
    }
  } else if (adFormat === 'SINGLE_IMAGE') {
    if (tiktokPlacement) issues.push('a video (images don\'t serve on the TikTok placement)');
    if (!f.resolvedImageId) issues.push('an image');
  } else if (adFormat === 'CAROUSEL_ADS') {
    const ids = Array.isArray(f.carouselImageIds) ? (f.carouselImageIds as string[]) : [];
    if (ids.length < 2) issues.push('at least 2 carousel images');
  }
  return issues;
}

/** Whole-tree issue list for the review step, in reading order. */
export function treeIssues(
  campaign: WorkspaceCampaign,
  adGroups: WorkspaceAdGroup[],
  identities?: Identity[],
): { campaign: string[]; groups: string[] } {
  const objective = (campaign.formData as Record<string, string>).objectiveType || campaign.objective || undefined;
  const groups: string[] = [];
  if (adGroups.length === 0) groups.push('Add at least one ad group.');
  for (const g of adGroups) {
    const gi = adGroupIssues(g, objective);
    if (gi.length) groups.push(`${g.name || 'An ad group'}: needs ${gi.join(', ')}.`);
    if (g.ads.length === 0) groups.push(`"${g.name}" has no ads.`);
    for (const a of g.ads) {
      const ai = adIssues(a, g, objective, identities);
      if (ai.length) groups.push(`${g.name} → ${a.name || 'untitled ad'}: needs ${ai.join(', ')}.`);
    }
  }
  return { campaign: campaignIssues(campaign), groups };
}

export function validLandingUrl(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  try { const url = new URL(value.trim()); return ['http:', 'https:'].includes(url.protocol) && !!url.hostname; }
  catch { return false; }
}
